"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function LatestArticlesConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4">
            <div className="bg-muted/20 aspect-[4/5] w-full animate-pulse rounded-sm" />
            <div className="space-y-2">
              <div className="bg-muted/20 h-6 w-3/4 animate-pulse rounded-sm" />
              <div className="bg-muted/20 h-4 w-full animate-pulse rounded-sm" />
              <div className="bg-muted/20 h-4 w-2/3 animate-pulse rounded-sm" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-muted-foreground py-12 text-center">
        <p>Unable to load articles.</p>
        <button onClick={refetch} className="text-primary mt-2 underline hover:no-underline">
          Try again
        </button>
      </div>;
  }
  if (posts.length === 0) {
    return <p className="text-muted-foreground py-12 text-center">No articles found.</p>;
  }
  return <div className="grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-3">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="group border-none bg-transparent transition-all duration-300 hover:shadow-[var(--shadow-lg)] hover:-translate-y-1 rounded-xl overflow-hidden">
          {post.featuredImage && <Link to={post.link || "#"} className="block w-full">
              <div className="overflow-hidden rounded-t-xl">
                <AspectRatio ratio={4 / 5}>
                  <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-all duration-500 saturate-[0.75] group-hover:scale-105 group-hover:saturate-100" loading="lazy" decoding="async" />
                </AspectRatio>
              </div>
            </Link>}

          <CardHeader className="px-4 pt-6 pb-2">
            <Link to={post.link || "#"}>
              <h3 className="font-serif text-[20px] font-bold leading-tight text-foreground transition-colors group-hover:text-[var(--chart-4)]" dangerouslySetInnerHTML={{
            __html: post.title
          }} />
            </Link>
          </CardHeader>

          <CardContent className="px-4 pb-4">
            <div className="text-muted-foreground line-clamp-2 text-sm leading-relaxed" dangerouslySetInnerHTML={{
          __html: post.excerpt || ""
        }} />
          </CardContent>

          <CardFooter className="px-4 pb-6 pt-0">
            <Link to={post.link || "#"} className="text-[var(--chart-4)] transition-transform duration-300 group-hover:translate-x-1" aria-label={`Read more about ${post.title}`}>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function LatestArticles() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="latest-articles" className="bg-background py-20 md:py-32">
      <div className="container mx-auto px-6 md:px-12">
        <div className="mb-16 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="font-serif text-4xl font-medium text-foreground md:text-5xl">
              Latest Articles
            </h2>
            <p className="text-muted-foreground mt-4 text-lg">
              Insights, strategies, and stories from the forefront of business growth.
            </p>
          </div>
          <Link to="/BlogPage" className="text-foreground hover:text-[var(--chart-4)] flex items-center gap-2 border-b border-transparent pb-0.5 text-sm font-medium transition-colors hover:border-[var(--chart-4)]">
            View all articles
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <LatestArticlesConsumer />
        </PostsProvider>
      </div>
    </section>;
}